// REST client for the Atlas Pages Functions API (Cloudflare D1 backend).
import type {
  Trip, Stop, Event, Booking, Reference,
  TripWithChildren, TripitRun, TripStatus,
} from './types';

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
  });
  if (!res.ok) {
    const txt = await res.text();
    throw new Error(`${init?.method ?? 'GET'} ${path} → ${res.status}: ${txt.slice(0, 200)}`);
  }
  if (res.status === 204) return undefined as unknown as T;
  return res.json() as Promise<T>;
}

// --- Trips ---

export const listTrips = () => req<Trip[]>('/api/trips');
export const getTrip   = (id: string) => req<TripWithChildren | null>(`/api/trips/${id}`).catch(e => {
  if (/→ 404/.test(String(e))) return null;
  throw e;
});
export const createTrip = (input: Partial<Trip>) =>
  req<Trip>('/api/trips', { method: 'POST', body: JSON.stringify(input) });
export const updateTrip = (id: string, patch: Partial<Trip>) =>
  req<Trip>(`/api/trips/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
export const deleteTrip = (id: string) =>
  req<{ ok: boolean }>(`/api/trips/${id}`, { method: 'DELETE' });

// --- Stops ---

export const addStop = (input: Partial<Stop> & { trip_id: string; city: string }) =>
  req<Stop>('/api/stops', { method: 'POST', body: JSON.stringify(input) });
export const updateStop = (id: string, patch: Partial<Stop>) =>
  req<Stop>(`/api/stops/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
export const deleteStop = (id: string) =>
  req<{ ok: boolean }>(`/api/stops/${id}`, { method: 'DELETE' });

// --- Events ---

export const addEvent = (input: Partial<Event> & { trip_id: string; title: string }) =>
  req<Event>('/api/events', { method: 'POST', body: JSON.stringify(input) });
export const deleteEvent = (id: string) =>
  req<{ ok: boolean }>(`/api/events/${id}`, { method: 'DELETE' });

// --- Bookings ---

export const addBooking = (input: Partial<Booking> & { trip_id: string; booking_type: Booking['booking_type'] }) =>
  req<Booking>('/api/bookings', { method: 'POST', body: JSON.stringify(input) });
export const deleteBooking = (id: string) =>
  req<{ ok: boolean }>(`/api/bookings/${id}`, { method: 'DELETE' });

// --- References ---

export const addReference = (input: Partial<Reference> & { trip_id: string }) =>
  req<Reference>('/api/references', { method: 'POST', body: JSON.stringify(input) });

// --- TripIt run ---

export const lastTripitRun = async (): Promise<TripitRun | null> => {
  try { return await req<TripitRun | null>('/api/tripit-runs'); }
  catch { return null; }
};

// --- AI extraction ---

export interface AIExtraction {
  title?: string; description?: string;
  start_date?: string; end_date?: string;
  city?: string; country?: string;
  event_type?: Event['event_type'];
  location_name?: string; raw?: any;
}

export const extractFromUrl = (url: string) =>
  req<AIExtraction>('/api/extract', { method: 'POST', body: JSON.stringify({ url }) });

// --- helpers ---

export function groupTripsByStatus(trips: Trip[]): Record<TripStatus, Trip[]> {
  const out = {
    idea: [], researching: [], planning: [],
    booked: [], in_progress: [], completed: [], archived: [],
  } as Record<TripStatus, Trip[]>;
  trips.forEach(t => out[t.status]?.push(t));
  return out;
}

export function tripDurationDays(t: Pick<Trip, 'start_date' | 'end_date'>): number | null {
  if (!t.start_date || !t.end_date) return null;
  const s = new Date(t.start_date).getTime();
  const e = new Date(t.end_date).getTime();
  return Math.max(1, Math.round((e - s) / 86400000) + 1);
}

export function daysUntil(d?: string | null): number | null {
  if (!d) return null;
  // Parse YYYY-MM-DD as a local date so "today" is 0 in the user's TZ, not -1.
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(d);
  const target = m
    ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
    : new Date(d);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.ceil((target.getTime() - today.getTime()) / 86400000);
}
